<template>
  <div>
    <ReportHeader
      v-if="!isLoading"
      :onDownload="onDownloadReport"
      :disabledDownload="disabledDownload"
      ref="reportHeader"
      :closestHeaderSection="closestHeaderSection"
      :jobWorkName="getJobWorkName()"
    />
    <template v-show="!isLoading">
      <div id="container-main" ref="sectionContainer">
        <template v-if="!isLoading">
          <repair-assessment-detail-dialog
            tool="report"
            :readOnly="!canEditReport"
            v-if="repairAssessmentVisible"
            @close="hideRepairAssessment"
            class="dij-ra-header-modal"
          />
          <DeviationReportModal
            :active="deviationReportVisible"
            @close="hideDeviationReport"
          />
          <section
            id="dij-header-section"
            v-if="isSectionVisible('projectInfo')"
          >
            <!-- Title, Customer, Customer Tag, Customer PO No  -->
            <div class="columns">
              <div class="column">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.project_title')"
                      itemToExcludeId="projectTitle"
                    />
                  </template>
                  <b-input
                    v-model="projectTitleText"
                    :disabled="!canEditReport"
                    size="is-medium"
                    :placeholder="$t('report_template.enter_project_number')"
                  ></b-input>
                </b-field>
              </div>

              <div class="column">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.customer')"
                      itemToExcludeId="customerInfoText"
                    />
                  </template>
                  <b-input
                    v-model="customerInfoText"
                    v-on:blur="onUpdateJobReport('customerInfoText')"
                    size="is-medium"
                    :disabled="!canEditReport"
                    :placeholder="
                      $t('report_template.enter_customer_information')
                    "
                  ></b-input>
                </b-field>
              </div>

              <div class="column">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.customer_tag')"
                      itemToExcludeId="customerTag"
                    />
                  </template>
                  <b-input
                    v-on:blur="onUpdateJobReport('customerTag')"
                    v-model="customerTag"
                    size="is-medium"
                    :disabled="!canEditReport"
                    :placeholder="$t('report_template.enter_customer_tag')"
                  ></b-input>
                </b-field>
              </div>

              <div class="column">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.customer_po_no')"
                      itemToExcludeId="customerPoNo"
                    />
                  </template>
                  <b-input
                    v-on:blur="onUpdateJobReport('customerPoNo')"
                    v-model="customerPoNo"
                    size="is-medium"
                    :disabled="!canEditReport"
                    :placeholder="$t('report_template.customer_po_no')"
                  ></b-input>
                </b-field>
              </div>
            </div>

            <!-- Unit Serial, Unit Model, Project Number, Sales Force Opportunity Number  -->
            <div class="columns">
              <div class="column">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.unit_serial')"
                      itemToExcludeId="serial"
                    />
                  </template>
                  <b-input
                    v-model="serialNumber"
                    v-on:blur="onUpdateJobReport('serialNumber')"
                    size="is-medium"
                    :placeholder="$t('report_template.enter_serial_info')"
                    :disabled="!canEditReport"
                  ></b-input>
                </b-field>
              </div>

              <div class="column">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.unit_model')"
                      itemToExcludeId="model"
                    />
                  </template>
                  <b-input
                    v-model="unitModel"
                    size="is-medium"
                    :disabled="!canEditReport"
                  ></b-input>
                </b-field>
              </div>

              <div class="column">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.project_number')"
                      itemToExcludeId="project"
                    />
                  </template>
                  <b-input
                    v-model="projectInfoText"
                    v-on:blur="onUpdateJobReport('projectInfoText')"
                    size="is-medium"
                    :placeholder="$t('report_template.enter_project_number')"
                    :disabled="!canEditReport"
                  ></b-input>
                </b-field>
              </div>

              <div class="column">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="
                        $t('report_template.sales_force_opportunity_number')
                      "
                      itemToExcludeId="salesForce"
                    />
                  </template>
                  <b-input
                    v-model="salesForceNumber"
                    v-on:blur="onUpdateJobReport('salesForceNumber')"
                    size="is-medium"
                    :placeholder="
                      $t('report_template.enter_sales_force_opportunity_number')
                    "
                    :disabled="!canEditReport"
                  ></b-input>
                </b-field>
              </div>
            </div>

            <!-- Process, Previous Orders, Contact Person Name, Contact Person Department  -->
            <div class="columns">
              <div class="column" v-if="getJobWorkName() === 'TCP'">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.process')"
                      itemToExcludeId="processText"
                    />
                  </template>
                  <b-input
                    v-model="processText"
                    v-on:blur="onUpdateJobReport('processText')"
                    size="is-medium"
                    :disabled="!canEditReport"
                    :placeholder="$t('report_template.enter_process')"
                  ></b-input>
                </b-field>
              </div>

              <div class="column">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.original_order_no')"
                      itemToExcludeId="originalOrderNo"
                    />
                  </template>
                  <b-input
                    v-model="originalOrderNo"
                    v-on:blur="onUpdateJobReport('originalOrderNo')"
                    size="is-medium"
                    :disabled="!canEditReport"
                    :placeholder="$t('report_template.enter_original_order_no')"
                  ></b-input>
                </b-field>
              </div>

              <div class="column">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.previous_orders')"
                      itemToExcludeId="previousOrdersText"
                    />
                  </template>
                  <b-input
                    v-model="previousOrdersText"
                    v-on:blur="onUpdateJobReport('previousOrdersText')"
                    size="is-medium"
                    :disabled="!canEditReport"
                    :placeholder="$t('report_template.enter_previous_orders')"
                  ></b-input>
                </b-field>
              </div>

              <div class="column" v-if="isSectionVisible('projectContacts')">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.contact_person_name')"
                      itemToExcludeId="contactPersonName"
                    />
                  </template>
                  <b-input
                    v-model="contactPersonName"
                    v-on:blur="onUpdateJobReport('contactPersonName')"
                    size="is-medium"
                    :disabled="!canEditReport"
                    :placeholder="
                      $t('report_template.enter_contact_person_name')
                    "
                  ></b-input>
                </b-field>
              </div>

              <div class="column" v-if="isSectionVisible('projectContacts')">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.contact_person_department')"
                      itemToExcludeId="contactPersonDepartment"
                    />
                  </template>
                  <b-input
                    v-model="contactPersonDepartment"
                    v-on:blur="onUpdateJobReport('contactPersonDepartment')"
                    size="is-medium"
                    :disabled="!canEditReport"
                    :placeholder="
                      $t('report_template.contact_person_department')
                    "
                  ></b-input>
                </b-field>
              </div>
            </div>

            <b-field
              v-if="isSectionVisible('summaryText')"
              style="flex-wrap: wrap"
            >
              <div id="lable-summary">
                <b-field style="margin-bottom: 0px">
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.executive_summary')"
                      itemToExcludeId="summaryText"
                    />
                  </template>
                </b-field>

                <div @click="onQuestionSummaryClick">
                  <b-icon id="icon-summary" icon="help-circle" />
                </div>
              </div>
              <div
                id="lable-summary"
                class="is-flex is-align-items-center"
                style="margin-top: 0.5em"
              >
                <b-tooltip
                  :label="$t('report_template.generate_executive_summary')"
                  position="is-top"
                >
                  <b-button
                    style="margin-left: 10px"
                    type="is-primary"
                    outlined
                    @click.stop="openChatBox"
                    :disabled="!canEditReport"
                  >
                    <img
                      src="/img/icons/rs_icon.png"
                      :alt="$t('diagram_builder.repair_icon')"
                      style="width: 1.5em; height: 1.5em; margin-bottom: 2px"
                    />
                    {{ $t('report_template.repair_select') }}
                  </b-button>
                </b-tooltip>
              </div>

              <div
                id="executive-summary-example"
                v-if="showSummaryExemple"
                style="margin-top: 10px"
              >
                {{ $t('report_template.sample_text') }} <br />
                {{ $t('report_template.sample_text_content') }}
              </div>
            </b-field>
            <div v-if="isSectionVisible('summaryText')" class="columns">
              <div class="column">
                <RichTextEditor
                  @onBlur="onUpdateJobReport('summaryText')"
                  v-model="summaryText"
                  :disabled="!canEditReport"
                />
              </div>
            </div>

            <div class="columns">
              <div class="column">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.revision_description')"
                      itemToExcludeId="revisionDescription"
                    />
                  </template>
                  <b-input
                    v-model="revisionDescription"
                    size="is-medium"
                    :readonly="!canEditReport"
                    :placeholder="
                      $t('report_template.enter_revision_description')
                    "
                    type="text"
                    required
                    ref="refRevisionDescription"
                    id="refRevisionDescription"
                    v-on:blur="onUpdateJobReport('revisionDescription')"
                  ></b-input>
                </b-field>
              </div>

              <div class="column">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.csm')"
                      itemToExcludeId="csmField"
                    />
                  </template>
                  <v-select
                    v-model="csmUserSelected"
                    size="is-large"
                    type="text"
                    :disabled="!canEditReport"
                    required
                    ref="refcsmField"
                    id="refcsmField"
                    :options="csm_usersList"
                    :getOptionLabel="
                      (u) => (u && u.id !== ' ' ? `${u.name} - ${u.id}` : '')
                    "
                    @input="onUpdateJobReport('csmUserSelected')"
                  ></v-select>
                </b-field>
              </div>
              <div class="column">
                <b-field>
                  <template slot="label">
                    <LabelInput
                      :label="$t('report_template.repair_engineer')"
                      itemToExcludeId="repairEngineer"
                    />
                  </template>
                  <v-select
                    v-model="reUserSelected"
                    size="is-large"
                    type="text"
                    :disabled="!canEditReport"
                    required
                    ref="refRepairEngineer"
                    id="refRepairEngineer"
                    :options="re_usersList"
                    :getOptionLabel="
                      (u) => (u && u.id !== ' ' ? `${u.name} - ${u.id}` : '')
                    "
                    @input="onUpdateJobReport('reUserSelected')"
                  ></v-select>
                </b-field>
              </div>
            </div>
          </section>

          <div
            class="deviation-report-container"
            id="dij-deviation-report-container"
            v-if="isInternalReport && isSectionVisible('deviationReport')"
          >
            <b-button
              type="is-primary"
              icon-left="plus"
              @click="addDeviationReport"
              v-if="!job.deviationReport"
              :disabled="!canEditReport"
              >{{ $t('report_header.deviation_report') }}</b-button
            >
            <template v-if="job.deviationReport">
              <b-field style="display: flex; gap: 10px">
                <template slot="label">
                  <LabelInput
                    :label="$t('report_header.deviation_report')"
                    itemToExcludeId="deviationReport"
                  />
                </template>
                <b-button
                  @click="showDeviationReport"
                  size="is-small"
                  :disabled="!canEditReport"
                  >{{ $t('edit') }}</b-button
                >
              </b-field>
            </template>

            <DeviationReport
              :key="deviationKey"
              v-if="job.deviationReport"
              :isReadonly="true"
              ref="deviationReport"
            />
          </div>
          <div
            class="repair-assessment"
            id="dij-repair-assessment"
            v-if="isSectionVisible('repairAssessment')"
          >
            <b-button
              type="is-primary"
              icon-left="plus"
              @click="showRepairAssessment"
              v-if="!job.repairAssessment"
              :disabled="!canEditReport"
              >{{ $t('report_header.repair_assessment') }}</b-button
            >

            <div
              v-if="job.repairAssessment"
              class="repair-assessment-container"
            >
              <b-field style="display: flex; gap: 10px">
                <template slot="label">
                  <LabelInput
                    :label="$t('report_header.repair_assessment')"
                    itemToExcludeId="repairAssessment"
                  />
                </template>
                <b-button
                  @click="showRepairAssessment"
                  size="is-small"
                  :disabled="!canEditReport"
                >
                  {{ $t('edit') }}</b-button
                >
              </b-field>

              <b-tabs type="is-boxed" :expanded="false" v-model="activeTab">
                <b-tab-item
                  :label="$t('report_template.major_components')"
                  icon="engine"
                  class="b-tab-item-scroll"
                >
                  <repair-assessment-detail
                    ref="repairAssessment"
                    v-model="filteredMajorRepairAssessment"
                    :read-only="true"
                    :tool="'editor'"
                  />
                </b-tab-item>
                <b-tab-item
                  :label="$t('report_template.minor_components')"
                  icon="screw-flat-top"
                  class="b-tab-item-scroll"
                >
                  <repair-assessment-detail
                    ref="repairAssessment"
                    v-model="filteredMinorRepairAssessment"
                    :read-only="true"
                    :tool="'editor'"
                    :isMinorComponents="true" /></b-tab-item
              ></b-tabs>
            </div>
          </div>

          <div
            class="assembly-overview"
            v-if="job.assemblyOverviewUrl && isSectionVisible('referenceImage')"
          >
            <div class="card-header">
              <p class="card-header-title">{{ $t('reference_image') }}</p>
            </div>
            <img
              :src="getAssemblyOverview()"
              style="max-height: 80vh; width: 100%; object-fit: contain"
            />
          </div>
          <section id="dij-work-performed">
            <b-field v-if="isSectionVisible('factFindingField')">
              <template slot="label">
                <LabelInput
                  :label="$t('report_template.work_performed_fact_finding')"
                  itemToExcludeId="factFindingField"
                />
              </template>
              <RichTextEditor
                @onBlur="onUpdateJobReport('factFindingField')"
                v-model="factFindingField"
                :disabled="!canEditReport"
              />
            </b-field>

            <b-field v-if="isSectionVisible('workPerformedRepairField')">
              <template slot="label">
                <LabelInput
                  :label="$t('report_template.work_performed_repair')"
                  itemToExcludeId="workPerformedRepairField"
                />
              </template>
              <RichTextEditor
                @onBlur="onUpdateJobReport('workPerformedRepairField')"
                v-model="workPerformedRepairField"
                :disabled="!canEditReport"
              />
            </b-field>

            <b-field
              v-if="isSectionVisible('repairsRecommendationsUpgradesField')"
            >
              <template slot="label">
                <LabelInput
                  :label="
                    $t(
                      'report_template.repairs_not_completed_recommendations_upgrades'
                    )
                  "
                  itemToExcludeId="repairsRecommendationsUpgradesField"
                />
              </template>
              <RichTextEditor
                @onBlur="
                  onUpdateJobReport('repairsRecommendationsUpgradesField')
                "
                v-model="repairsRecommendationsUpgradesField"
                :disabled="!canEditReport"
              />
            </b-field>
          </section>

          <template
            v-if="
              isSectionVisible('inspectionTasks') ||
              isSectionVisible('additionalTasks')
            "
          >
            <label
              class="label"
              style="
                margin-top: 30px;
                font-weight: bold;
                font-size: 1.5em;
                margin-bottom: 10px;
              "
            >
              {{ $t('tasks') }}</label
            >
            <template v-for="task in tasksInOrder">
              <Instruction
                :key="task._id"
                v-if="task.taskType === 'instruction'"
                :taskProp="task"
                :id="task._id"
              />
              <Diagram
                :key="task._id"
                v-if="task.taskType === 'diagram'"
                :taskProp="task"
                :unit="displayUnit"
                :isExclude="excludedItems.includes(task._id)"
              />
            </template>
          </template>
          <div class="appendix-class" v-if="isSectionVisible('appendix')">
            <b-field>
              <template slot="label">
                <LabelInput
                  :label="$t('report_template.appendix')"
                  itemToExcludeId="appendix"
                />
              </template>
              <ExternalData :readOnly="!canEditReport" />
            </b-field>
          </div>
        </template>
      </div>
    </template>
    <image-gallery
      v-if="isGalleryDisplayed"
      v-model="galleryImages"
      @close="hideGallery"
      :startIndex="gallaryStartIndex"
      :reportMode="true"
      @onEditInformation="editImageInformation"
    />
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>
  </div>
</template>

<script>
import { v4 } from 'uuid';
import throttle from 'lodash/throttle';
import ReportHeader from './ReportHeader';
import Instruction from './tasks/Instruction';
import Diagram from './tasks/Diagram';
import LabelInput from './components/LabelInput';
import ExternalData from './components/ExternalData';
import DIJVueConfigurations from '../../helpers/DIJVueConfigurations';
import RepairAssessmentDetail from '../../components/layout/RepairAssessmentDetail';
import RepairAssessmentDetailDialog from '../../components/layout/RepairAssessmentDetailDialog';
import DeviationReport from './components/DeviationReport';
import DeviationReportModal from './components/DeviationReportModal';
import ImageGallery from '../../components/layout/ImageGallery';
import RichTextEditor from '../../components/RichTextEditor';

import ReportDataParserMixin from '../../mixins/Report/ReportDataParserMixin';
import ExcludeItemsMixin from '../../mixins/Report/ExcludeItemsMixin';
import PhaseSectionsMixin from '../../mixins/Report/PhaseSectionsMixin';
import { UPDATE_JOB_REPORT } from '../../store/jobs/actions/actionTypes';
import {
  REPORTS_GET_REPORT_VIEW,
  REPORTS_UPDATE_ATTACHMENT_IMAGE,
} from '../../store/reports/actions/actionTypes';
import { SET_GALLERY_IMAGES } from '../../store/reports/mutations/mutationTypes';
import { IS_INTERNAL_REPORT } from '../../store/reports/getters/getterTypes';
import client from '../../config/api';
import ExecutiveSummaryStrategy from '../../components/ChatBox/strategies/ExecutiveSummaryStrategy';
import { GET_CURRENT_JOB } from '../../store/jobs/getters/getterTypes';
import { acquireToken } from '../../services/auth';
import { SEND_WEBSOCKET_MESSAGE } from '../../store/websocket/actions/actionTypes';
import { GET_CURRENT_DISPLAY_UNIT } from '../../store/diagram/getters/getterTypes';
import { USERS_GET_USERS } from '../../store/users/actionTypes';
import { GET_USERS } from '../../store/users/getters/getterTypes';
import { FETCH_JOB_PHASES } from '../../store/jobPhases/actions/actionTypes';

const uuidv4 = v4;

const ALL_SECTION_KEYS = [
  'deviationReport',
  'projectInfo',
  'summaryText',
  'revisionHistory',
  'projectContacts',
  'tableOfContents',
  'generalInformation',
  'referenceImage',
  'repairAssessment',
  'findingsRAT',
  'factFindingField',
  'workPerformedRepairField',
  'repairsRecommendationsUpgradesField',
  'recommendedWorkRepair',
  'newParts',
  'inspectionTasks',
  'additionalTasks',
  'appendix',
];

export default {
  name: 'report-template',
  mixins: [ReportDataParserMixin, ExcludeItemsMixin, PhaseSectionsMixin],
  components: {
    ReportHeader,
    Instruction,
    Diagram,
    LabelInput,
    ExternalData,
    RepairAssessmentDetail,
    RepairAssessmentDetailDialog,
    DeviationReport,
    DeviationReportModal,
    ImageGallery,
    RichTextEditor,
  },
  data() {
    return {
      isLoading: true,
      repairAssessmentVisible: false,
      companyInfoText: '',
      customerTag: '',
      customerPoNo: '',
      customerInfoText: '',
      serialNumber: '',
      unitModel: '',
      projectInfoText: '',
      projectTitleText: '',
      processText: '',
      previousOrdersText: '',
      originalOrderNo: '',
      salesForceNumber: '',
      summaryText: '',
      showSummaryExemple: false,
      readyToDownload: false,
      docFileUrl: '',
      filteredRepairAssessment: [],
      disabledDownload: false,
      revisionDescription: '',
      csmField: '',
      repairEngineer: '',
      activeTab: 0,
      deviationReportVisible: false,
      deviationKey: 0,
      isGalleryDisplayed: false,
      contactPersonDepartment: '',
      contactPersonName: '',
      factFindingField: '',
      workPerformedRepairField: '',
      repairsRecommendationsUpgradesField: '',
      closestHeaderSection: null,
      csm_usersList: [],
      re_usersList: [],
      csmUserSelected: [],
      reUserSelected: [],
    };
  },
  computed: {
    job() {
      return this.$store.getters[GET_CURRENT_JOB];
    },
    tasksInOrder() {
      const { tasks } = this.$store.state.ReportsModule.reportView;
      const arr = Array.isArray(tasks) ? tasks : [];
      return arr.filter((t) => this.isTaskVisibleForPhase(t));
    },

    excludedItems() {
      return this.$store.state.ReportsModule.excludedItems;
    },
    isInternalReport() {
      return this.$store.getters[IS_INTERNAL_REPORT];
    },
    filteredMinorRepairAssessment: {
      get() {
        return this.job.repairAssessment.filter((row) => row.isMinor);
      },
      set(value) {
        this.filteredRepairAssessment = [
          ...this.filteredRepairAssessment,
          value,
        ];
      },
    },
    filteredMajorRepairAssessment: {
      get() {
        return this.job.repairAssessment.filter((row) => !row.isMinor);
      },
      set(value) {
        this.filteredRepairAssessment = [
          ...this.filteredRepairAssessment,
          value,
        ];
      },
    },
    galleryImages() {
      const images = [];
      this.$store.state.ReportsModule.galleryImages.forEach((img) => {
        images.push(img.image);
      });
      return images;
    },

    gallaryStartIndex() {
      return this.$store.state.ReportsModule.gallaryStartIndex;
    },
    users() {
      return this.$store.getters[GET_USERS];
    },
    displayUnit() {
      return this.$store.getters[GET_CURRENT_DISPLAY_UNIT];
    },
    canEditReport() {
      return this.$store.getters.canEditJob(this.job, 'report');
    },
  },
  methods: {
    getAssemblyOverview() {
      return window.location.host.includes('localhost')
        ? `http://localhost:4566/images${this.job.assemblyOverviewUrl}`
        : this.job.assemblyOverviewUrl;
    },

    openChatBox() {
      this.$chatbox.open({
        title: 'Generate Executive Summary',
        message: 'Generating executive summary...',
        strategy: new ExecutiveSummaryStrategy(),
        metadata: this.job,
      });
    },
    onQuestionSummaryClick() {
      this.showSummaryExemple = !this.showSummaryExemple;
    },
    async onUpdateJobReport() {
      if (!this.canEditReport) {
        return;
      }

      const payload = {
        _id: this.job._id,
        version: this.job.version,
        reportUpdate: true,
        repairEngineer: this.reUserSelected?.name,
        csmField: this.csmUserSelected?.name,
        revisionDescription: this.revisionDescription,
        summaryText: this.summaryText,
        salesForceNumber: this.salesForceNumber,
        projectInfoText: this.projectInfoText,
        serialNumber: this.serialNumber,
        customerInfoText: this.customerInfoText,
        companyInfoText: this.companyInfoText,
        factFindingField: this.factFindingField,
        customerTag: this.customerTag,
        customerPoNo: this.customerPoNo,
        originalOrderNo: this.originalOrderNo,
        previousOrdersText: this.previousOrdersText,
        contactPersonName: this.contactPersonName,
        contactPersonDepartment: this.contactPersonDepartment,
        processText: this.processText,
        workPerformedRepairField: this.workPerformedRepairField,
        repairsRecommendationsUpgradesField:
          this.repairsRecommendationsUpgradesField,

        repairEngineerUser: Array.isArray(this.reUserSelected)
          ? this.reUserSelected
          : [this.reUserSelected],
        csmUser: Array.isArray(this.csmUserSelected)
          ? this.csmUserSelected
          : [this.csmUserSelected],
      };

      await this.$store.dispatch(UPDATE_JOB_REPORT, payload);
    },
    prepareExportJsonFile() {
      const data = [];
      this.tasksInOrder.forEach((task) => {
        if (task.taskType === 'diagram') {
          const taskCanvasDiagram = document.getElementById(
            `canvas-diagram-${task._id}`
          );
          if (!taskCanvasDiagram) return;
          // element.canvas = taskCanvasDiagram.toDataURL('image/jpeg');
          const canvas = taskCanvasDiagram.toDataURL();
          data.push({
            id: task._id,
            canvas,
          });
        }
      });

      return data;
    },
    // getAttachments(task, allAttachmentsImagesUrls) {
    //   const attachments = [];

    //   if (task.taskType === 'diagram') {
    //     const parseData = this.getAttachemntsDiagram(task).filter((att) => {
    //       return !att.isFreeFinding;
    //     });
    //     parseData.forEach((att) => {
    //       const images = [];
    //       if (att.images) {
    //         att.images.forEach((img) => {
    //           if (!this.isImageExclude(img.img)) {
    //             allAttachmentsImagesUrls.push(img.img);

    //             const imageObj = {
    //               img: img.img,
    //               imgDim: img.imgDim,
    //               // description: img.imgDescription ? img.imgDescription : '',
    //             };

    //             if (img.imgDescription) {
    //               imageObj.description = img.imgDescription;
    //             }

    //             if (img.imgName && !img.imgName.startsWith('picture_')) {
    //               imageObj.name = img.imgName;
    //             }
    //             images.push(imageObj);
    //           }
    //         });
    //       }
    //       const label = att.name;
    //       const attachment = {
    //         att_array: images,
    //         label: `Label ${label}`,
    //       };
    //       if (att.findingsByInspector) {
    //         attachment.finding = `${att.findingsByInspector}<br>`;
    //       }
    //       attachments.push(attachment);
    //     });
    //   }

    //   if (task.taskType === 'instruction') {
    //     const parsedData = this.getAttachemntsInstruction(task);
    //     parsedData.forEach((att) => {
    //       if (att.images && att.images.length > 0) {
    //         const attItem = {
    //           images: [],
    //         };
    //         attItem.question = att.question ? att.question : '';

    //         att.images.forEach((img) => {
    //           if (!this.isImageExclude(img.img)) {
    //             const desc = img.imgDescription ? img.imgDescription : '';
    //             const imageObj = {
    //               img: img.img,
    //               imgDim: img.imgDim,
    //               imgDescription: `Description: ${desc}`,
    //             };

    //             if (img.imgName && !img.imgName.startsWith('picture_')) {
    //               imageObj.imgName = img.imgName;
    //             }
    //             attItem.images.push(imageObj);
    //             allAttachmentsImagesUrls.push(img.img);
    //           }
    //         });
    //         attachments.push(attItem);
    //       }
    //     });
    //   }

    //   return attachments;
    // },
    editImageInformation(info) {
      const image = this.$store.state.ReportsModule.galleryImages[info.index];
      this.$store.dispatch(REPORTS_UPDATE_ATTACHMENT_IMAGE, {
        bubbleId: image.bubbleId,
        imageUrl: image.image.url || image.image.src || image.image.path,
        imgDescription: info.description,
        imageName: info.name,
      });
    },
    async onDownloadReport() {
      const isValid = this.$refs.refRevisionDescription.checkHtml5Validity();
      if (!isValid) {
        const element = document.getElementById('refRevisionDescription');
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      this.disabledDownload = true;
      try {
        const reportId = uuidv4();
        const jobId = this.job._id;
        const canvasDiagrams = this.prepareExportJsonFile();
        let jsonFileName = null;
        if (canvasDiagrams.length > 0) {
          jsonFileName = `${reportId}_json.txt`;
          const blob = new Blob([JSON.stringify(canvasDiagrams)], {
            type: 'text/plain',
          });
          const file = new File([blob], jsonFileName);
          await this.uploadToS3(file, 'json');
        }

        const isCustomerReport = !this.isInternalReport;

        const sectionExclusions = this.activeReportSections
          ? ALL_SECTION_KEYS.filter(
              (key) => !this.activeReportSections.includes(key)
            )
          : [];

        const allTasks = this.$store.state.ReportsModule.reportView.tasks || [];
        const visibleTaskIds = new Set(this.tasksInOrder.map((t) => t._id));
        const taskExclusions = allTasks
          .filter((t) => !visibleTaskIds.has(t._id))
          .map((t) => t._id);

        const mergedExcludedItems = [
          ...new Set([
            ...this.excludedItems,
            ...sectionExclusions,
            ...taskExclusions,
          ]),
        ];

        const jsonData = {
          reportId,
          jobId,
          api_endpoint: process.env.VUE_APP_API_ENDPOINT,
          revisionDescription: this.revisionDescription,
          reportDate: Date.now(),
          jsonFileName,
          isCustomerReport,
          excludedItems: mergedExcludedItems,
        };

        const token = await acquireToken();
        this.$store.dispatch(SEND_WEBSOCKET_MESSAGE, {
          action: 'jobsReportDownload',
          data: {
            jsonData,
            headers: {
              authorization: token,
            },
          },
        });
        this.startPolling(reportId);
      } catch (error) {
        console.error(error);
        alert('Something went wrong when trying to generate report');
        this.disabledDownload = false;
      }
    },
    async uploadToS3(file, path) {
      const dijConfigurations = new DIJVueConfigurations();
      const url = await dijConfigurations.uploadFileToS3(file, file.name, path);
      return url;
    },
    async startPolling(reportId) {
      let attempts = 0;
      const maxAttempts = 300;
      const pollInterval = 5000;
      const poll = setInterval(async () => {
        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(poll);
          this.status = 'Polling stopped. Server not ready.';
          throw new Error('Error when generating file');
        }

        const serverStatus = await this.checkServerStatus(reportId, poll);

        if (serverStatus.readyToDownload) {
          this.disabledDownload = false;
          clearInterval(poll);
          this.readyToDownload = true;
          this.docFileUrl = serverStatus.path;
          this.triggerDownload(serverStatus.path);
          await this.$store.dispatch('getJob', this.job._id);
          this.$store.commit('SET_PROGRESS_BAR', {
            key: 'report-loading',
            value: null,
            message: '',
          });
        }
      }, pollInterval);
    },
    async checkServerStatus(reportId, poll) {
      try {
        const response = await client.get(
          `/job/${this.job._id}/report/${reportId}`
        );
        return response.data;
      } catch (error) {
        console.error('Error checking server status:', error);
        this.disabledDownload = false;
        clearInterval(poll);
        this.readyToDownload = true;
        await this.$store.dispatch('getJob', this.job._id);
        this.$store.commit('SET_PROGRESS_BAR', {
          key: 'report-loading',
          value: null,
          message: '',
        });
        alert('Something went wrong when trying to generate report');
        return 'Not Ready';
      }
    },
    triggerDownload(downloadLink) {
      const anchor = document.createElement('a');
      anchor.style.display = 'none';
      anchor.href = downloadLink;
      anchor.setAttribute('download', '');

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    },
    showRepairAssessment() {
      if (this.job.repairAssessment) {
        this.job.repairAssessment.forEach((ra) => {
          if (ra.serialNumber === undefined && !ra.isMinor) {
            ra.serialNumber = '';
          }
          if (ra.assembly === undefined) {
            ra.assembly = '';
          }
          if (ra.itemNumber === undefined) {
            ra.itemNumber = '';
          }
        });
      }
      this.repairAssessmentVisible = true;
    },
    hideRepairAssessment() {
      this.repairAssessmentVisible = false;

      const event = new Event('ra-update');
      document.dispatchEvent(event);
    },
    addDeviationReport() {
      const updatedJob = { ...this.job };
      updatedJob.deviationReport = {
        radio: 'review_only',
        rows: [],
      };
      this.$store.dispatch('updateJob', updatedJob);
    },
    showDeviationReport() {
      this.deviationReportVisible = true;
    },
    hideDeviationReport() {
      this.deviationReportVisible = false;
      this.deviationKey += 1;
    },
    hideGallery() {
      this.isGalleryDisplayed = false;
      this.$store.commit(SET_GALLERY_IMAGES, []);
    },
    setJobData() {
      this.companyInfoText = this.job.companyInfoText;
      this.customerInfoText = this.job.customerInfoText;
      if (Array.isArray(this.job.model)) {
        this.unitModel = this.job.model[0] ? this.job.model[0].text : '';
      } else {
        this.unitModel = this.job.model;
      }
      this.projectInfoText = this.job.projectInfoText;
      this.serialNumber = this.job.serialNumber;
      this.salesInfoText = this.job.salesInfoText;
      this.salesForceNumber = this.job.salesForceNumber;
      this.summaryText = this.job.summaryText;
      this.revisionDescription = this.job.revisionDescription;
      this.csmField = this.job.csmField;
      this.repairEngineer = this.job.repairEngineer;
      this.processText = this.job.processText;
      this.originalOrderNo = this.job.originalOrderNo;
      this.customerTag = this.job.customerTag;
      this.customerPoNo = this.job.customerPoNo;
      this.contactPersonName = this.job.contactPersonName;
      this.contactPersonDepartment = this.job.contactPersonDepartment;
      this.factFindingField = this.job.factFindingField || '';
      this.workPerformedRepairField = this.job.workPerformedRepairField || '';
      this.repairsRecommendationsUpgradesField =
        this.job.repairsRecommendationsUpgradesField || '';

      if (this.job.repairAssessment) {
        const repairAssessment = [];
        this.job.repairAssessment.forEach((element) => {
          if (element.id) {
            repairAssessment.push({ ...element });
          } else {
            repairAssessment.push({ id: uuidv4(), ...element });
          }
        });
        this.filteredRepairAssessment = repairAssessment;
      }

      if (this.job.executingSite) {
        this.$store.dispatch(FETCH_JOB_PHASES, {
          siteId: this.job.executingSite,
        });
      }
    },
    onScroll() {
      if (this.$refs.reportHeader.dropDownTasks) {
        let closest = null;
        let closestDistance = Infinity;
        this.$refs.reportHeader.dropDownTasks.forEach((section) => {
          const el = document.getElementById(section._id);
          if (!el) return;
          const rect = el.getBoundingClientRect();
          const distance = Math.abs(rect.top);

          if (rect.top <= 190 && distance < closestDistance) {
            closest = section._id;
            closestDistance = distance;
          }
        });
        this.closestHeaderSection = closest;
      }
    },

    getJobWorkName() {
      if (!this.job) return '';
      if (!this.job.workOrderName) return '';

      if (
        typeof this.job.workOrderName === 'string' ||
        this.job.workOrderName instanceof String
      )
        return this.job.workOrderName;

      if (Array.isArray(this.job.workOrderName))
        return this.job.workOrderName[0].text;
    },
  },

  watch: {
    repairAssessmentVisible(value) {
      if (!value) {
        this.$store
          .dispatch('getRepairAssessment', this.job)
          .then((repairAssessment) => {
            this.repairAssessment = [...repairAssessment];
            this.filteredRepairAssessment = [...repairAssessment];
          });
      }
    },
    galleryImages(images) {
      if (!images || images.length === 0) {
        this.isGalleryDisplayed = false;
      } else {
        this.isGalleryDisplayed = true;
      }
    },
    job() {
      this.setJobData();
    },
  },

  async created() {
    this.isLoading = true;
    const { id } = this.$route.params;
    await this.$store.dispatch('getJob', id);
    await this.$store.dispatch(REPORTS_GET_REPORT_VIEW, id);

    this.setJobData();
    this.companyInfoText = this.job.companyInfoText;
    this.customerInfoText = this.job.customerInfoText;
    if (Array.isArray(this.job.model)) {
      this.unitModel = this.job.model[0] ? this.job.model[0].text : '';
    } else {
      this.unitModel = this.job.model;
    }
    this.projectInfoText = this.job.projectInfoText;
    this.projectTitleText = this.job.title || '';
    this.serialNumber = this.job.serialNumber;
    this.processText = this.job.processText || '';
    this.previousOrdersText = this.job.previousOrdersText || '';
    this.originalOrderNo = this.job.originalOrderNo || '';
    this.salesForceNumber = this.job.salesForceNumber;
    this.summaryText = this.job.summaryText;
    this.revisionDescription = this.job.revisionDescription;
    this.csmField = this.job.csmField;
    this.repairEngineer = this.job.repairEngineer;
    this.customerTag = this.job.customerTag;
    this.customerPoNo = this.job.customerPoNo;
    this.contactPersonName = this.job.contactPersonName;
    this.contactPersonDepartment = this.job.contactPersonDepartment;
    this.factFindingField = this.job.factFindingField || '';
    this.workPerformedRepairField = this.job.workPerformedRepairField || '';
    this.repairsRecommendationsUpgradesField =
      this.job.repairsRecommendationsUpgradesField || '';

    if (this.job.repairAssessment) {
      const repairAssessment = [];
      this.job.repairAssessment.forEach((element) => {
        if (element.id) {
          repairAssessment.push({ ...element });
        } else {
          repairAssessment.push({ id: uuidv4(), ...element });
        }
      });
      this.filteredRepairAssessment = repairAssessment;
    }
    this.isLoading = false;
    this.$store.commit('SET_PROGRESS_BAR', {
      key: 'report-loading',
      value: null,
      message: '',
    });
    this.$refs.sectionContainer.addEventListener(
      'scroll',
      throttle(this.onScroll, 100)
    );
  },

  async mounted() {
    await this.$store.dispatch(USERS_GET_USERS);

    this.csm_usersList = this.users.map((u) => ({
      id: u._id,
      name: u.fullname,
    }));

    this.re_usersList = this.users.map((u) => ({
      id: u._id,
      name: u.fullname,
    }));
    const repairEngineerFallback = {
      name: this.job.repairEngineer?.trim() || '',
      id: ' ',
    };

    const csmFallback = {
      name: this.job.csmField?.trim() || '',
      id: ' ',
    };

    this.reUserSelected =
      Array.isArray(this.job.repairEngineerUser) &&
      this.job.repairEngineerUser.length > 0 &&
      this.job.repairEngineerUser[0]?.id
        ? this.job.repairEngineerUser
        : [repairEngineerFallback];

    this.csmUserSelected =
      Array.isArray(this.job.csmUser) &&
      this.job.csmUser.length > 0 &&
      this.job.csmUser[0]?.id
        ? this.job.csmUser
        : [csmFallback];
  },
  beforeDestroy() {
    this.$refs.sectionContainer.removeEventListener('scroll', this.onScroll);
    this.$chatbox.close();
  },
};
</script>

<style scoped lang="scss">
#dij-work-performed {
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-top: 30px;
}

.assembly-overview {
  margin-top: 20px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  flex-direction: column;
}

.space-ra {
  height: 50px;
}

#container-main {
  padding: 30px 10px;
  overflow: hidden;
  overflow-y: scroll;
  overflow-x: hidden;
  height: calc(100vh - 105px);
}

#lable-summary {
  display: flex;
  align-items: center;
}

#icon-summary {
  margin-left: 5px;
}

#revisionContainer {
  width: 50%;
}

#executive-summary-example {
  max-width: 600px;
  margin: 20px;
}

.repair-assessment {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  width: 100%;
  flex-direction: column;
  background-color: #fff;
}

.repair-assessment-container {
  background-color: #fff;
  margin-top: 20px;
}

.deviation-report-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  width: 100%;
  flex-direction: column;
  background-color: #fff;
}

.appendix-class {
  padding-left: 10px;
  background: white;
}

::v-deep .vs__dropdown-toggle {
  background: #fff !important;
  font-size: 1.25rem;
}
</style>
